import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const STEPS = [
  { num: '01', title: 'Partagez votre lien', desc: 'Envoyez votre lien personnel à un ami propriétaire qui n\'est pas encore sur Havenly.', color: '#E8A87C', bg: '#FDF0E6' },
  { num: '02', title: 'Il publie son annonce', desc: 'Votre ami crée son compte, publie son logement et profite de 10% de commission pendant 3 mois.', color: 'var(--green)', bg: 'var(--green-light)' },
  { num: '03', title: 'Il complète sa 1ère réservation', desc: 'Dès que son premier voyageur a effectué son check-out, la réservation est validée.', color: '#534AB7', bg: '#EEEDFE' },
  { num: '04', title: 'Vous recevez 30€', desc: '30€ sont crédités sur votre compte Havenly et déduits automatiquement de vos prochaines commissions.', color: 'var(--ink)', bg: 'var(--surface-2)' },
];

const FAQS = [
  { q: 'Quand est-ce que je reçois mes 30€ ?', r: 'Dès que votre filleul complète sa première réservation (check-out confirmé). Le crédit apparaît sur votre compte Havenly dans les 24h et est déduit automatiquement de vos prochaines commissions.' },
  { q: 'Combien de fois puis-je parrainer ?', r: 'Illimité ! Chaque parrainage réussi vous rapporte 30€ de crédit. Plus vous parrainez, plus vous économisez sur vos commissions.' },
  { q: 'Mon ami bénéficie-t-il aussi d\'un avantage ?', r: 'Oui — votre filleul profite d\'une commission réduite à 10% au lieu de 12% pendant ses 3 premiers mois sur Havenly.' },
  { q: 'Que se passe-t-il si mon ami ne complète pas de réservation ?', r: 'Aucun crédit n\'est attribué. Le déclencheur est la première réservation complétée (check-out confirmé sans litige).' },
  { q: 'Puis-je cumuler plusieurs crédits ?', r: 'Oui, les crédits s\'accumulent sur votre compte Havenly et sont déduits au fur et à mesure de vos factures de commission. Pas de date d\'expiration.' },
];

export default function ReferralPage({ onNavigate }) {
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [referralCode, setReferralCode] = useState('');

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session?.user) return;
      const { data: profile } = await supabase
        .from('host_profiles')
        .select('referral_code, referral_credits, full_name')
        .eq('id', session.user.id)
        .single();
      if (profile) {
        // Utiliser le code en base, sinon générer un fallback lisible
        const code = profile.referral_code ||
          `${(profile.full_name || 'HOTE').split(' ')[0].toUpperCase()}-HAVENLY-2025`;
        setReferralCode(code);
        // Si pas de code en base, le créer
        if (!profile.referral_code) {
          await supabase.from('host_profiles')
            .update({ referral_code: code })
            .eq('id', session.user.id);
        }
      }
    });
  }, []);

  const referralLink = `https://havenly.fr/rejoindre?code=${referralCode}`;

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const sendInvite = () => {
    if (!email.trim()) return;
    setSent(true);
    setEmail('');
    setTimeout(() => setSent(false), 3000);
  };

  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh', paddingTop: 60 }}>

      {/* HERO */}
      <section style={{ background: 'linear-gradient(135deg, #FDF0E6 0%, #ffffff 60%)', padding: '4rem 2rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-20%', right: '-5%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(232,168,124,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 400px', gap: 60, alignItems: 'center' }}>

          <div>
            <div style={{ display: 'inline-block', background: '#FDF0E6', color: '#C4784A', fontSize: 12, fontWeight: 500, padding: '5px 14px', borderRadius: 20, marginBottom: 20, border: '1px solid #E8A87C' }}>
              Programme de parrainage
            </div>
            <h1 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(32px, 5vw, 52px)', letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 16 }}>
              Parrainez un hôte,<br />
              <em style={{ color: '#C4784A', fontStyle: 'italic' }}>gagnez 30€</em>
            </h1>
            <p style={{ fontSize: 16, color: 'var(--ink-3)', lineHeight: 1.8, marginBottom: 28, maxWidth: 480 }}>
              Invitez un ami à publier son logement sur Havenly. Dès sa première réservation complétée, vous recevez <strong style={{ color: 'var(--ink)', fontWeight: 500 }}>30€ de crédit commission</strong> — déduit automatiquement de vos prochaines factures.
            </p>

            {/* Stats */}
            <div style={{ display: 'flex', gap: 32, marginBottom: 32 }}>
              {[
                { val: '30€', label: 'Crédit pour vous', color: '#C4784A' },
                { val: '10%', label: 'Commission filleul 3 mois', color: 'var(--green-mid)' },
                { val: '∞', label: 'Parrainages illimités', color: '#534AB7' },
              ].map(s => (
                <div key={s.label}>
                  <div style={{ fontSize: 24, fontWeight: 500, color: s.color }}>{s.val}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>

            <button onClick={() => onNavigate('dashboard')} style={{ padding: '10px 24px', background: 'transparent', color: 'var(--ink-3)', border: '1px solid var(--border-2)', borderRadius: 24, fontSize: 14, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
              ← Retour au dashboard
            </button>
          </div>

          {/* Carte lien */}
          <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 20, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
            <h3 style={{ fontSize: 15, fontWeight: 500, marginBottom: 16 }}>Votre lien de parrainage</h3>

            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 12, fontSize: 13, color: 'var(--ink-3)', wordBreak: 'break-all', lineHeight: 1.5 }}>
              {referralLink}
            </div>

            <button onClick={copyLink} style={{
              width: '100%', padding: '12px',
              background: copied ? 'var(--green)' : '#E8A87C',
              color: '#fff', border: 'none', borderRadius: 10,
              fontSize: 14, fontWeight: 500, cursor: 'pointer',
              fontFamily: 'var(--sans)', marginBottom: 16,
              transition: 'background 0.2s',
            }}>
              {copied ? '✓ Lien copié !' : 'Copier le lien'}
            </button>

            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {[
                { label: 'WhatsApp', bg: '#25D366' },
                { label: 'Email', bg: 'var(--ink)' },
                { label: 'SMS', bg: '#007AFF' },
              ].map(s => (
                <button key={s.label} onClick={() => {
                  const msg = `Je loue sur Havenly, une plateforme française avec moins de commissions. Rejoins-moi : ${referralLink}`;
                  if (s.label === 'WhatsApp') window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`);
                  else if (s.label === 'Email') window.open(`mailto:?subject=Rejoins Havenly&body=${encodeURIComponent(msg)}`);
                  else window.open(`sms:?body=${encodeURIComponent(msg)}`);
                }} style={{
                  flex: 1, padding: '8px', background: s.bg, color: '#fff',
                  border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'var(--sans)',
                }}>
                  {s.label}
                </button>
              ))}
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>Ou invitez par email</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="email" placeholder="email@exemple.fr" value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendInvite()}
                  style={{ flex: 1, padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontFamily: 'var(--sans)', outline: 'none' }} />
                <button onClick={sendInvite} style={{
                  padding: '9px 16px', background: sent ? 'var(--green)' : '#E8A87C',
                  color: '#fff', border: 'none', borderRadius: 8, fontSize: 13,
                  fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)',
                  transition: 'background 0.2s', whiteSpace: 'nowrap',
                }}>
                  {sent ? '✓ Envoyé' : 'Envoyer'}
                </button>
              </div>
            </div>

            <div style={{ marginTop: 12, textAlign: 'center', fontSize: 11, color: 'var(--ink-4)' }}>
              Code personnel : <strong style={{ color: 'var(--ink)', fontWeight: 500 }}>{referralCode}</strong>
            </div>
          </div>
        </div>
      </section>

      {/* ÉTAPES */}
      <section style={{ padding: '4rem 2rem', background: 'var(--white)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(24px, 3.5vw, 36px)', letterSpacing: '-0.5px', marginBottom: 32, textAlign: 'center' }}>
            Comment ça marche
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {STEPS.map(step => (
              <div key={step.num} style={{ display: 'grid', gridTemplateColumns: '80px 1fr', background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden', borderLeft: `4px solid ${step.color}` }}>
                <div style={{ background: step.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontFamily: 'var(--serif)', fontSize: 22, color: step.color }}>{step.num}</span>
                </div>
                <div style={{ padding: '18px 22px' }}>
                  <h3 style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>{step.title}</h3>
                  <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.65 }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AVANTAGES FILLEUL */}
      <section style={{ padding: '4rem 2rem', background: 'var(--surface)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(22px, 3vw, 32px)', letterSpacing: '-0.5px', marginBottom: 8, textAlign: 'center' }}>
            Ce que votre filleul reçoit
          </h2>
          <p style={{ fontSize: 14, color: 'var(--ink-3)', textAlign: 'center', marginBottom: 28 }}>
            Un avantage concret pour l'inciter à rejoindre Havenly
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
            {[
              { title: '10% de commission', desc: 'Au lieu de 12% pendant les 3 premiers mois — soit ~240€ économisés sur 10 000€ de revenus.', color: 'var(--green)', bg: 'var(--green-light)' },
              { title: 'Emma IA incluse', desc: 'Répond aux voyageurs en 4 langues, gère le calendrier et les codes d\'accès dès le 1er jour.', color: '#534AB7', bg: '#EEEDFE' },
              { title: 'Versement sous 24h', desc: 'Encaissement 24h après chaque check-out — bien plus rapide que les concurrents.', color: '#E8A87C', bg: '#FDF0E6' },
              { title: 'Publication vérifiée', desc: 'Annonce relue et validée par l\'équipe Havenly sous 24h pour un lancement optimal.', color: 'var(--ink)', bg: 'var(--surface-2)' },
            ].map(a => (
              <div key={a.title} style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, borderTop: `3px solid ${a.color}` }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: a.bg, marginBottom: 12 }} />
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 6 }}>{a.title}</div>
                <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>{a.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: '4rem 2rem', background: 'var(--white)' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(22px, 3vw, 32px)', letterSpacing: '-0.5px', marginBottom: 32, textAlign: 'center' }}>
            Questions fréquentes
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {FAQS.map((faq, i) => (
              <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{
                  width: '100%', padding: '16px 20px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 500,
                  color: 'var(--ink)', textAlign: 'left',
                }}>
                  {faq.q}
                  <span style={{ fontSize: 18, color: 'var(--ink-3)', flexShrink: 0, marginLeft: 12, transition: 'transform 0.2s', display: 'inline-block', transform: openFaq === i ? 'rotate(45deg)' : 'none' }}>+</span>
                </button>
                {openFaq === i && (
                  <div style={{ padding: '0 20px 16px', fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.7, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                    {faq.r}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '4rem 2rem', background: 'var(--ink)', textAlign: 'center' }}>
        <div style={{ maxWidth: 500, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(24px, 4vw, 38px)', color: '#fff', letterSpacing: '-0.8px', marginBottom: 16 }}>
            Prêt à parrainer ?
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)', marginBottom: 32, lineHeight: 1.7 }}>
            Copiez votre lien et partagez-le — c'est tout ce qu'il faut.
          </p>
          <button onClick={copyLink} style={{ padding: '13px 32px', background: '#E8A87C', color: '#fff', border: 'none', borderRadius: 28, fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
            {copied ? '✓ Lien copié !' : 'Copier mon lien de parrainage →'}
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background: 'var(--ink)', borderTop: '0.5px solid rgba(255,255,255,0.08)', padding: '1.5rem 2rem' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <svg viewBox="0 0 210 52" style={{ height: 22, width: 'auto', display: 'block' }} xmlns="http://www.w3.org/2000/svg">
            <text x="0" y="44" fontFamily="Georgia,'Times New Roman',serif" fontSize="52" fontWeight="400" fontStyle="italic" letterSpacing="-2" fill="white">haven<tspan fill="#1D9E75" dx="-3">ly</tspan></text>
          </svg>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>© 2026 Havenly · Mentions légales · CGU · Confidentialité</div>
          <div style={{ display: 'flex', gap: 20 }}>
            {['Support', 'Presse', 'Investisseurs'].map(l => (
              <span key={l} style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', cursor: 'pointer' }}>{l}</span>
            ))}
          </div>
        </div>
      </footer>

    </div>
  );
}
